/**
 * 默认配置与合并逻辑
 */

import type { TrackerConfig, ResolvedTrackerConfig } from '../types/config'

/**
 * 默认配置
 */
export const DEFAULT_CONFIG: Omit<ResolvedTrackerConfig, 'reportUrl' | 'appName' | 'appVersion'> = {
  appKey: '',
  appChannel: '',
  // 容器信息（SDK外部传入）
  containerType: '',
  containerVersion: '',
  webviewVersion: '',
  containerAppKeys: [],
  deviceBrand: '',
  deviceModel: '',
  enableAutoPageView: true,
  enableAutoClick: false,
  enableExposure: false,
  exposureThreshold: 0.5,
  exposureDelay: 100,
  debug: false,
  sampleRate: 1,
  batchSize: 1,
  batchInterval: 0,
  userId: '',
  staffStationId: '',
  // 网络监控默认配置
  enableNetworkMonitor: true,
  networkMaxRequestsPerPage: 50,
  networkMaxPages: 1,
  networkMaxBodySize: 10240,
  networkMaxResponseSize: 102400,
  networkRecordHeaders: false,
  networkRecordBody: true,
  networkRecordResponse: true,
  networkExcludeUrls: [],
  // 动态配置默认值
  configUrl: '', // 动态配置远程地址
  // 页面上报模式默认值
  pageReportMode: 'full',
  // 自动曝光扫描默认值
  enableAutoExposure: false,
  autoExposureMaxElements: 100,
  autoExposureScanRoot: '',
  // 接口签名默认值
  enableSign: true,
  signSecret: '',
  // 上报配置默认值
  supportKeepAlive: true,
}

/**
 * 合并配置
 * @param userConfig 用户配置
 * @returns 完整配置
 */
export function mergeConfig(userConfig: TrackerConfig): ResolvedTrackerConfig {
  return {
    ...DEFAULT_CONFIG,
    ...userConfig,
    // 确保必填字段存在
    reportUrl: userConfig.reportUrl,
    appName: userConfig.appName,
    appVersion: userConfig.appVersion,
  }
}

/**
 * 验证配置
 * @param config 配置对象
 * @throws 配置无效时抛出错误
 */
export function validateConfig(config: TrackerConfig): void {
  if (!config.reportUrl) {
    throw new Error('[XT-Tracker] reportUrl is required')
  }
  if (!config.appName) {
    throw new Error('[XT-Tracker] appName is required')
  }
  if (!config.appVersion) {
    throw new Error('[XT-Tracker] appVersion is required')
  }

  // 验证采样率
  if (config.sampleRate !== undefined) {
    if (config.sampleRate < 0 || config.sampleRate > 1) {
      throw new Error('[XT-Tracker] sampleRate must be between 0 and 1')
    }
  }

  // 验证曝光阈值
  if (config.exposureThreshold !== undefined) {
    if (config.exposureThreshold < 0 || config.exposureThreshold > 1) {
      throw new Error('[XT-Tracker] exposureThreshold must be between 0 and 1')
    }
  }

  // 验证 routePathResolver
  if (config.routePathResolver !== undefined && typeof config.routePathResolver !== 'function') {
    throw new Error('[XT-Tracker] routePathResolver must be a function')
  }

  // 验证 referrerResolver
  if (config.referrerResolver !== undefined && typeof config.referrerResolver !== 'function') {
    throw new Error('[XT-Tracker] referrerResolver must be a function')
  }

  // 验证 pageReportMode
  if (config.pageReportMode !== undefined
    && config.pageReportMode !== 'full'
    && config.pageReportMode !== 'configOnly') {
    throw new Error('[XT-Tracker] pageReportMode must be "full" or "configOnly"')
  }
}
